// Licensed to the Apache Software Foundation (ASF) under one
// or more contributor license agreements.  See the NOTICE file
// distributed with this work for additional information
// regarding copyright ownership.  The ASF licenses this file
// to you under the Apache License, Version 2.0 (the
// "License"); you may not use this file except in compliance
// with the License.  You may obtain a copy of the License at
//
//   http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing,
// software distributed under the License is distributed on an
// "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
// KIND, either express or implied.  See the License for the
// specific language governing permissions and limitations
// under the License.

<template>
  <div>
    <!-- ── Month selector ────────────────────────────────────────────────── -->
    <a-card size="small" style="margin-bottom: 12px;">
      <a-row justify="space-between" align="middle">
        <a-col>
          <a-space>
            <a-button :disabled="loading" @click="shiftMonth(-1)">
              <template #icon><LeftOutlined /></template>
            </a-button>
            <a-date-picker
              v-model:value="selectedMonth"
              picker="month"
              :disabled-date="disabledFutureMonth"
              :allowClear="false"
              format="MMMM YYYY"
              style="width: 160px;"
              @change="fetchAll"
            />
            <a-button :disabled="loading || isCurrentMonth" @click="shiftMonth(1)">
              <template #icon><RightOutlined /></template>
            </a-button>
          </a-space>
        </a-col>
        <a-col>
          <a-button :loading="loading" @click="fetchAll">
            <template #icon><ReloadOutlined /></template>
            {{ $t('label.refresh') }}
          </a-button>
        </a-col>
      </a-row>
    </a-card>

    <a-spin :spinning="loading">

      <!-- ── Limit bars — forced single row via flex ────────────────────── -->
      <a-card size="small" style="margin-bottom: 12px;">
        <div class="limits-row">
          <div
            v-for="item in limitItems"
            :key="item.key"
            class="limit-item"
          >
            <div class="limit-header">
              <span class="limit-label" :title="$t(item.labelKey)">{{ $t(item.labelKey) }}</span>
              <span class="limit-used" :class="getUsedClass(item.pct)">{{ item.usedDisplay }}</span>
            </div>
            <a-progress
              :percent="item.pct"
              :show-info="false"
              size="small"
              :stroke-color="getBarColor(item.pct)"
              trail-color="#f0f0f0"
            />
            <div class="limit-footer">
              <span>{{ item.availDisplay }} left</span>
              <span class="limit-max">{{ item.limitDisplay === 'Unlimited' ? '∞' : '/ ' + item.limitDisplay }}</span>
            </div>
          </div>
        </div>
      </a-card>

      <!-- ── Chart 1: Time-based (VM hours + IP hours + Network hours) ───── -->
      <a-card size="small" style="margin-bottom: 12px;">
        <template #title>
          {{ monthLabel }} — {{ $t('label.usage.hours') || 'Usage Hours' }}
        </template>
        <div v-if="hasTimeData" style="position: relative; height: 260px;">
          <canvas ref="timeChartCanvas"></canvas>
        </div>
        <a-empty v-else :description="'No time-based usage for ' + monthLabel" />
      </a-card>

      <!-- Charts 2 & 3: Storage and Network side-by-side on wide screens -->
      <div class="side-by-side-charts">
        <a-card size="small" class="side-chart-card">
          <template #title>
            {{ monthLabel }} — {{ $t('label.storage') || 'Storage' }} (GB·h)
          </template>
          <div v-if="hasStorageData" style="position: relative; height: 200px;">
            <canvas ref="storageChartCanvas"></canvas>
          </div>
          <a-empty v-else :description="'No storage usage for ' + monthLabel" />
        </a-card>

        <a-card size="small" class="side-chart-card">
          <template #title>
            {{ monthLabel }} — {{ $t('label.network') || 'Network Transfer' }} (MB)
          </template>
          <div v-if="hasNetworkData" style="position: relative; height: 200px;">
            <canvas ref="networkChartCanvas"></canvas>
          </div>
          <a-empty v-else :description="'No network transfer data for ' + monthLabel" />
        </a-card>
      </div>

      <!-- ── VM detail table ────────────────────────────────────────────── -->
      <a-card
        v-if="vmDetails.length > 0"
        size="small"
        :title="$t('label.instances') || 'Virtual Machines'"
      >
        <a-table
          :dataSource="vmDetails"
          :columns="vmColumns"
          :pagination="{ pageSize: 50, showSizeChanger: true }"
          size="small"
          rowKey="usageid"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'hours'">
              <a-tag color="blue">{{ record.totalHours }}h</a-tag>
            </template>
            <template v-else-if="column.key === 'resources'">
              <a-tag>{{ record.cpunumber }} vCPU</a-tag>
              <a-tag>{{ formatMemory(record.memory) }}</a-tag>
            </template>
          </template>
        </a-table>
      </a-card>

    </a-spin>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick, getCurrentInstance } from 'vue'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { ReloadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons-vue'
import { Chart, BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js'
import { postAPI, getAPI } from '@/api'
import ChartCard from '@/components/widgets/ChartCard'

dayjs.extend(utc)
Chart.register(BarController, BarElement, CategoryScale, LinearScale, Tooltip, Legend)

const UT = {
  RUNNING_VM:             1,
  IP_ADDRESS:             3,
  NETWORK_BYTES_SENT:     4,
  NETWORK_BYTES_RECEIVED: 5,
  VOLUME:                 6,
  NETWORK_OFFERING:       30
}

const LIMIT_DEFS = [
  { key: 'vm',               labelKey: 'label.instances',         format: v => String(v)                                },
  { key: 'cpu',              labelKey: 'label.cpunumber',         format: v => String(v)                                },
  { key: 'memory',           labelKey: 'label.memory',            format: v => parseFloat(v / 1024).toFixed(1) + ' G'  },
  { key: 'primarystorage',   labelKey: 'label.primary.storage',   format: v => parseFloat(v).toFixed(0) + ' G'         },
  { key: 'secondarystorage', labelKey: 'label.secondary.storage', format: v => parseFloat(v).toFixed(0) + ' G'         },
  { key: 'ip',               labelKey: 'label.public.ips',        format: v => String(v)                                },
  { key: 'network',          labelKey: 'label.networks',          format: v => String(v)                                },
  { key: 'vpc',              labelKey: 'label.vpcs',              format: v => String(v)                                },
  { key: 'volume',           labelKey: 'label.volumes',           format: v => String(v)                                },
  { key: 'snapshot',         labelKey: 'label.snapshots',         format: v => String(v)                                }
]

// Shared Chart.js options factory
const makeBarOptions = (stacked, extraOptions = {}) => ({
  responsive:          true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', labels: { boxWidth: 12, padding: 14 } },
    tooltip: { mode: 'index', intersect: false }
  },
  scales: {
    x: { stacked, grid: { display: false }, ticks: { maxRotation: 0 } },
    y: { stacked, beginAtZero: true, ticks: { precision: 0 } }
  },
  ...extraOptions
})

export default {
  name: 'UsageMetricsDashboard',
  components: { ChartCard, ReloadOutlined, LeftOutlined, RightOutlined },
  props: {
    accountId: { type: String, default: null },
    domainId:  { type: String, default: null },
    projectId: { type: String, default: null }
  },

  setup (props) {
    const loading           = ref(false)
    const selectedMonth     = ref(dayjs().startOf('month'))
    const allRecords        = ref([])
    const account           = ref({})
    const timeChartCanvas    = ref(null)
    const storageChartCanvas = ref(null)
    const networkChartCanvas = ref(null)
    let   timeChart    = null
    let   storageChart = null
    let   networkChart = null

    // Access store to detect active project context (mirrors UserLimits.vue behaviour)
    const { proxy } = getCurrentInstance()
    const store = proxy.$store

    // Resolve which project/account is in scope, in priority order:
    // 1. Explicit projectId prop (e.g. embedded on a project detail page)
    // 2. Active project in store (user has switched to a project)
    // 3. Explicit accountId prop (embedded on an account detail page)
    // 4. No filter — current user's own account (default dashboard)
    const activeProjectId = computed(() =>
      props.projectId || store.getters.project?.id || null
    )

    const monthLabel     = computed(() => selectedMonth.value.format('MMMM YYYY'))
    const isCurrentMonth = computed(() => selectedMonth.value.isSame(dayjs(), 'month'))
    const shiftMonth     = (delta) => { selectedMonth.value = selectedMonth.value.add(delta, 'month'); fetchAll() }
    const disabledFutureMonth = (date) => date && date > dayjs().endOf('month')

    // ── Limit bar items ───────────────────────────────────────────────────
    const limitItems = computed(() =>
      LIMIT_DEFS.map(def => {
        const total = account.value[def.key + 'total']
        const limit = account.value[def.key + 'limit']
        const avail = account.value[def.key + 'available']
        const unlimited = (limit === 'Unlimited' || limit === '-1' || limit === undefined)
        const pct = unlimited ? 0 : Math.min(Math.round((total / limit) * 100), 100)
        return {
          key:          def.key,
          labelKey:     def.labelKey,
          pct:          isNaN(pct) ? 0 : pct,
          usedDisplay:  total !== undefined ? def.format(total) : '—',
          availDisplay: unlimited ? '∞' : (avail !== undefined ? def.format(avail) : '—'),
          limitDisplay: unlimited ? 'Unlimited' : (limit !== undefined ? def.format(limit) : '—')
        }
      }).filter(item => item.usedDisplay !== '—')
    )

    const getBarColor  = (pct) => pct >= 90 ? '#ff4d4f' : pct >= 75 ? '#faad14' : '#52c41a'
    const getUsedClass = (pct) => pct >= 90 ? 'used-critical' : pct >= 75 ? 'used-warning' : ''

    // ── Daily data ────────────────────────────────────────────────────────
    const byType = (type) => allRecords.value.filter(r => r.usagetype === type)

    const dailyData = computed(() => {
      const n = selectedMonth.value.daysInMonth()
      return Array.from({ length: n }, (_, i) => {
        const d          = i + 1
        const dateStr    = selectedMonth.value.date(d).format('YYYY-MM-DD')
        const recs       = allRecords.value.filter(r => r.startdate?.startsWith(dateStr))
        const sumType    = (type) => recs.filter(r => r.usagetype === type).reduce((s, r) => s + parseFloat(r.rawusage || 0), 0)
        const storageGbH = recs.filter(r => r.usagetype === UT.VOLUME)
          .reduce((s, r) => s + parseFloat(r.rawusage || 0) * (r.size || 0) / (1024 ** 3), 0)
        return {
          label:        String(d),
          vmHours:      Math.round(sumType(UT.RUNNING_VM) * 10) / 10,
          ipHours:      Math.round(sumType(UT.IP_ADDRESS) * 10) / 10,
          networkHours: Math.round(sumType(UT.NETWORK_OFFERING) * 10) / 10,
          storageGbH:   Math.round(storageGbH * 10) / 10,
          bytesSentMb:  Math.round(sumType(UT.NETWORK_BYTES_SENT)     / (1024 ** 2) * 100) / 100,
          bytesRecvMb:  Math.round(sumType(UT.NETWORK_BYTES_RECEIVED) / (1024 ** 2) * 100) / 100
        }
      })
    })

    const hasTimeData    = computed(() => dailyData.value.some(d => d.vmHours > 0 || d.ipHours > 0 || d.networkHours > 0))
    const hasStorageData = computed(() => dailyData.value.some(d => d.storageGbH > 0))
    const hasNetworkData = computed(() => dailyData.value.some(d => d.bytesSentMb > 0 || d.bytesRecvMb > 0))

    // ── Chart rendering ───────────────────────────────────────────────────
    const destroyAll = () => {
      [timeChart, storageChart, networkChart].forEach(c => c && c.destroy())
      timeChart = storageChart = networkChart = null
    }

    const renderCharts = () => {
      destroyAll()
      const labels = dailyData.value.map(d => d.label)

      // Chart 1 — time-based hours (stacked)
      if (timeChartCanvas.value && hasTimeData.value) {
        timeChart = new Chart(timeChartCanvas.value, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              { label: 'VM Hours',      data: dailyData.value.map(d => d.vmHours),      backgroundColor: 'rgba(24,  144, 255, 0.75)', borderWidth: 0, borderRadius: 2 },
              { label: 'IP Hours',      data: dailyData.value.map(d => d.ipHours),      backgroundColor: 'rgba( 82, 196,  26, 0.75)', borderWidth: 0, borderRadius: 2 },
              { label: 'Network Hours', data: dailyData.value.map(d => d.networkHours), backgroundColor: 'rgba(250, 173,  20, 0.75)', borderWidth: 0, borderRadius: 2 }
            ]
          },
          options: makeBarOptions(true)
        })
      }

      // Chart 2 — storage GB·h (single series, no stacking needed)
      if (storageChartCanvas.value && hasStorageData.value) {
        storageChart = new Chart(storageChartCanvas.value, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              { label: 'Storage GB·h', data: dailyData.value.map(d => d.storageGbH), backgroundColor: 'rgba(114, 46, 209, 0.75)', borderWidth: 0, borderRadius: 2 }
            ]
          },
          options: makeBarOptions(false)
        })
      }

      // Chart 3 — network TX / RX in MB (grouped, not stacked, so both bars visible)
      if (networkChartCanvas.value && hasNetworkData.value) {
        networkChart = new Chart(networkChartCanvas.value, {
          type: 'bar',
          data: {
            labels,
            datasets: [
              { label: 'TX (MB)', data: dailyData.value.map(d => d.bytesSentMb), backgroundColor: 'rgba(235,  47, 150, 0.75)', borderWidth: 0, borderRadius: 2 },
              { label: 'RX (MB)', data: dailyData.value.map(d => d.bytesRecvMb), backgroundColor: 'rgba( 19, 194, 194, 0.75)', borderWidth: 0, borderRadius: 2 }
            ]
          },
          options: makeBarOptions(false) // grouped so TX and RX bars sit side-by-side
        })
      }
    }

    watch(dailyData, () => nextTick(renderCharts))

    // ── VM table ──────────────────────────────────────────────────────────
    const vmDetails = computed(() => {
      const map = new Map()
      for (const r of byType(UT.RUNNING_VM)) {
        if (!map.has(r.usageid)) map.set(r.usageid, { ...r, totalHours: parseFloat(r.rawusage || 0) })
        else map.get(r.usageid).totalHours += parseFloat(r.rawusage || 0)
      }
      return [...map.values()]
        .map(r => ({ ...r, totalHours: Math.round(r.totalHours) }))
        .sort((a, b) => b.totalHours - a.totalHours)
    })

    const vmColumns = [
      { title: 'Name',      dataIndex: 'name',          key: 'name'      },
      { title: 'OS',        dataIndex: 'osdisplayname', key: 'os'        },
      { title: 'Type',      dataIndex: 'type',          key: 'type'      },
      { title: 'Resources', dataIndex: 'cpunumber',     key: 'resources' },
      { title: 'Hours',     dataIndex: 'totalHours',    key: 'hours',    sorter: (a, b) => a.totalHours - b.totalHours }
    ]

    // ── API ───────────────────────────────────────────────────────────────
    const fetchAll = async () => {
      loading.value    = true
      allRecords.value = []
      try {
        await Promise.all([fetchAccount(), fetchUsageRecords()])
      } finally {
        loading.value = false
      }
    }

    const fetchAccount = async () => {
      try {
        if (activeProjectId.value) {
          // In project context: use listProjects which returns the same
          // vmtotal/vmlimit/vmavailable fields on the project object
          const json = await getAPI('listProjects', { id: activeProjectId.value })
          const list = json?.listprojectsresponse?.project
          if (list?.length > 0) account.value = list[0]
        } else {
          const params = {}
          if (props.accountId) params.id = props.accountId
          const json = await getAPI('listAccounts', params)
          const list = json?.listaccountsresponse?.account
          if (list?.length > 0) account.value = list[0]
        }
      } catch (_) { account.value = {} }
    }

    const fetchUsageRecords = async () => {
      const month = selectedMonth.value  // capture reactively at call time
      const base = {
        startdate: month.startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        enddate:   month.endOf('month').format('YYYY-MM-DD HH:mm:ss'),
        pagesize: 1, page: 1
      }
      if (activeProjectId.value) {
        // listUsageRecords requires projectid (not accountid) for project scope
        base.projectid = activeProjectId.value
      } else {
        if (props.accountId) base.accountid = props.accountId
        if (props.domainId)  base.domainid  = props.domainId
      }

      const countJson = await postAPI('listUsageRecords', base)
      const total     = countJson?.listusagerecordsresponse?.count || 0
      if (total === 0) return

      const pageSize  = 500
      const pages     = await Promise.all(
        Array.from({ length: Math.ceil(total / pageSize) }, (_, i) =>
          postAPI('listUsageRecords', { ...base, page: i + 1, pagesize: pageSize })
            .then(j => j?.listusagerecordsresponse?.usagerecord || [])
            .catch(() => [])
        )
      )
      allRecords.value = pages.flat()
    }

    const formatMemory = (mb) => {
      if (!mb) return '0 MB'
      return mb >= 1024 ? (mb / 1024).toFixed(1) + ' GB' : mb + ' MB'
    }

    onMounted(() => fetchAll())
    onBeforeUnmount(() => destroyAll())

    return {
      loading, selectedMonth,
      timeChartCanvas, storageChartCanvas, networkChartCanvas,
      monthLabel, isCurrentMonth,
      limitItems, hasTimeData, hasStorageData, hasNetworkData,
      shiftMonth, disabledFutureMonth, fetchAll,
      vmDetails, vmColumns,
      getBarColor, getUsedClass, formatMemory
    }
  }
}
</script>

<style lang="less" scoped>
// ── Limits — single flex row, items shrink equally ────────────────────────
.limits-row {
  display: flex;
  flex-direction: row;
  gap: 0;
  width: 100%;
  overflow: hidden; // never wraps
}

.limit-item {
  flex: 1 1 0;        // all items share space equally
  min-width: 0;       // allow shrinking below content width
  padding: 0 10px;
  border-right: 1px solid #f0f0f0;

  &:first-child { padding-left: 0; }
  &:last-child  { padding-right: 0; border-right: none; }
}

.limit-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 4px;
  margin-bottom: 4px;
}

.limit-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
}

.limit-used {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
  flex-shrink: 0;

  &.used-warning  { color: #faad14; font-weight: 600; }
  &.used-critical { color: #ff4d4f; font-weight: 600; }
}

.limit-footer {
  display: flex;
  justify-content: space-between;
  margin-top: 2px;
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  white-space: nowrap;
}

.limit-max {
  color: #bfbfbf;
}

// ── Side-by-side storage + network charts ─────────────────────────────────
.side-by-side-charts {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.side-chart-card {
  min-width: 0; // prevent overflow inside grid cell
}
</style>